/**
 * IO Service Orders (studio view) — "My requests" list of every
 * order this studio has submitted from the IO Services catalogue.
 *
 * Read-only: studios can see status updates the platform admin
 * makes (new → contacted → in_progress → completed) but can't
 * change status themselves. The admin contacts them out-of-band
 * by phone / email; this page is just a tracker.
 *
 * Future: add a "Cancel" button + a cancellation reason for the
 * studio to back out before the admin has touched it.
 */
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import { useFullOrganization } from '@/lib/useFullOrganization';
import {
  getPlatformServiceOrderStatusConfig,
  type PlatformServiceOrder,
} from '@/lib/platformServiceOrders';
import { getPlatformServiceCategoryConfig } from '@/lib/platformServices';
import { usePlatformServiceOrders } from '@/lib/usePlatformServiceOrders';

export function IOServiceOrdersPage() {
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { query: searchQuery, setPlaceholder } = useTopbarSearch();
  const [localSearch, setLocalSearch] = useState('');

  useEffect(() => {
    setPlaceholder('Search your requests…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  const { data, loading } = usePlatformServiceOrders({
    scope: 'studio',
    orgId,
    searchText: (searchQuery || localSearch).trim(),
  });

  return (
    <div className="page active" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>My IO Service requests</h1>
          <div className="projects-stats">
            {data.length} request{data.length === 1 ? '' : 's'}
          </div>
        </div>
        <NavLink to="/io-services" className="btn-primary">
          <i className="ph ph-plus" style={{ fontSize: 14 }} /> Browse services
        </NavLink>
      </div>

      <div className="filter-bar">
        <div className="filter-search-wrap" style={{ flex: 1 }}>
          <i className="ph ph-magnifying-glass" />
          <input
            className="filter-search"
            type="search"
            placeholder="Search by service or contact…"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.currentTarget.value)}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 32, color: 'var(--text-secondary)' }}>Loading…</div>
      ) : data.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}
        >
          <i className="ph ph-tray" style={{ fontSize: 48, opacity: 0.5 }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No requests yet</div>
          <div style={{ marginTop: 4, fontSize: 14 }}>
            Browse IO Services to request floor protection, websites, and more.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data.map((o) => (
            <OrderRow key={o.id} order={o} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderRow({ order }: { order: PlatformServiceOrder }) {
  const catCfg = getPlatformServiceCategoryConfig(order.serviceCategory);
  const stCfg = getPlatformServiceOrderStatusConfig(order.status);
  const submittedAt = order.createdAt?.toMillis?.() ?? 0;
  const submittedLabel = submittedAt
    ? new Date(submittedAt).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

  return (
    <div
      className="card"
      style={{
        padding: 14,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        display: 'grid',
        gridTemplateColumns: '40px 1fr auto',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: catCfg.tintBg,
          color: catCfg.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
        }}
      >
        <i className={catCfg.icon} />
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{order.serviceTitle}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
          {order.projectName ? `${order.projectName} · ` : ''}
          Submitted {submittedLabel}
        </div>
        {order.adminNotes ? (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: 'var(--text-primary)',
              background: 'var(--surface-muted, #f8fafc)',
              padding: '6px 8px',
              borderRadius: 6,
            }}
          >
            <strong>Note from InteriorIO:</strong> {order.adminNotes}
          </div>
        ) : null}
        {order.quotedPrice && order.quotedPrice > 0 ? (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: 'var(--text-secondary)',
            }}
          >
            Quoted: <strong>₹{order.quotedPrice.toLocaleString('en-IN')}</strong>
          </div>
        ) : null}
      </div>
      <span
        style={{
          padding: '4px 10px',
          borderRadius: 999,
          background: stCfg.tintBg,
          color: stCfg.color,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {stCfg.label}
      </span>
    </div>
  );
}
